import React, {useState, useMemo, useEffect} from 'react';
import {
    Box,
    Grid,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    TextField,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    Button,
    Divider,
    Paper,
    Tooltip,
    IconButton,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Drawer,
    Fab,
    Autocomplete,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';

import FolderIcon from '@mui/icons-material/Folder';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import SecurityIcon from '@mui/icons-material/Security';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {toast} from 'react-toastify';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import api, {configApi} from 'utils/api';

const categoryIcons = {
    essential: <SettingsIcon/>,
    database: <StorageIcon/>,
    paths: <FolderIcon/>,
    auth: <LockIcon/>,
    mail: <EmailIcon/>,
    kakao: <ChatIcon/>,
    security: <SecurityIcon/>,
};

const productLabels = {
    'into-sign': 'Into Sign',
    'into-view': 'Into View',
};

const styles = {
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 3,
    },
    metaForm: {
        mb: 3,
        p: 2,
        borderRadius: 1,
    },
    container: {
        display: 'flex',
        gap: 3,
        flexDirection: {xs: 'column', md: 'row'},
    },
    categoryList: {
        width: {xs: '100%', md: 240},
        flexShrink: 0,
    },
    categoryPaper: {
        p: 1,
        position: {md: 'sticky'},
        top: {md: 80},
    },
    content: {
        flexGrow: 1,
        minWidth: 0,
    },
    section: {
        mb: 4,
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: '1.1rem',
        mb: 0.5,
    },
    sectionDesc: {
        color: 'text.secondary',
        fontSize: '0.875rem',
        mb: 2,
    },
    fieldRow: {
        mb: 2,
    },
    fieldLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mb: 0.5,
    },
    fieldHint: {
        color: 'text.secondary',
        fontSize: '0.75rem',
        mt: 0.5,
    },
    actions: {
        display: 'flex',
        gap: 2,
        justifyContent: 'flex-end',
        mt: 4,
        pt: 3,
        borderTop: 1,
        borderColor: 'divider',
        flexWrap: 'wrap',
    },
    fab: {
        position: 'fixed',
        bottom: 16,
        right: 16,
        display: {xs: 'flex', md: 'none'},
    },
};

export default function ConfigEditor({schemas, editingPreset, onClose, onSaveComplete}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // 제품 선택
    const productKeys = Object.keys(schemas);
    const [selectedProduct, setSelectedProduct] = useState(
        editingPreset?.product || productKeys[0] || ''
    );

    // 메타 정보
    const [client, setClient] = useState(editingPreset?.client || '');
    const [serverName, setServerName] = useState(editingPreset?.server_name || '');
    const [description, setDescription] = useState(editingPreset?.description || '');

    // 고객사 자동완성 목록
    const [clientOptions, setClientOptions] = useState([]);

    // 현재 스키마
    const schema = schemas[selectedProduct];

    // 설정 값
    const [selectedCategory, setSelectedCategory] = useState(schema?.categories[0]?.id || '');
    const deriveVirtualFields = (data) => {
        if (!schema) return data;
        const result = {...data};
        schema.categories.forEach(cat => {
            cat.properties.forEach(prop => {
                if (prop.virtual && prop.options) {
                    const match = prop.options.find(opt => {
                        if (!opt.linkedValues) return false;
                        return Object.entries(opt.linkedValues).every(
                            ([k, v]) => result[k] === v
                        );
                    });
                    result[prop.key] = match ? match.value : prop.default;
                }
            });
        });
        return result;
    };

    const [values, setValues] = useState(() => {
        if (editingPreset?.configData) {
            return deriveVirtualFields(editingPreset.configData);
        }
        const initial = {};
        if (schema) {
            schema.categories.forEach(cat => {
                cat.properties.forEach(prop => {
                    initial[prop.key] = prop.default;
                });
            });
        }
        return initial;
    });

    const [showPasswords, setShowPasswords] = useState({});
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    // ENC 암호화 상태 (필드별, alwaysEncrypt 필드는 기본 ON)
    const [encryptFields, setEncryptFields] = useState(() => {
        const initial = {};
        if (schema) {
            schema.categories.forEach(cat => {
                cat.properties.forEach(prop => {
                    if (prop.alwaysEncrypt) initial[prop.key] = true;
                });
            });
        }
        return initial;
    });
    const [encrypting, setEncrypting] = useState(false);

    const toggleEncryptField = (key) => {
        setEncryptFields(prev => ({...prev, [key]: !prev[key]}));
    };

    // 고객사 목록 로드
    useEffect(() => {
        api.get('/api/config/clients')
            .then(res => setClientOptions(res.data))
            .catch(() => {
            });
    }, []);

    // 제품 변경 시 값 초기화
    useEffect(() => {
        if (schema && !editingPreset) {
            const initial = {};
            schema.categories.forEach(cat => {
                cat.properties.forEach(prop => {
                    initial[prop.key] = prop.default;
                });
            });
            setValues(initial);
            setSelectedCategory(schema.categories[0]?.id || '');
        }
    }, [selectedProduct, schema, editingPreset]);

    const handleChange = (key, value) => {
        setValues(prev => {
            const next = {...prev, [key]: value};
            // Apply linkedValues for select properties
            if (schema) {
                const allProps = schema.categories.flatMap(c => c.properties);
                const prop = allProps.find(p => p.key === key);
                if (prop?.options) {
                    const selectedOpt = prop.options.find(opt =>
                        (typeof opt === 'string' ? opt : opt.value) === value
                    );
                    if (selectedOpt?.linkedValues) {
                        Object.assign(next, selectedOpt.linkedValues);
                    }
                }
            }
            return next;
        });
    };

    const togglePasswordVisibility = (key) => {
        setShowPasswords(prev => ({...prev, [key]: !prev[key]}));
    };

    const isFieldVisible = (prop) => {
        if (!prop.dependsOn) return true;
        return Boolean(values[prop.dependsOn]);
    };

    const getEncryptEntries = () => {
        if (!schema) return [];
        const entries = [];
        schema.categories.forEach(cat => {
            cat.properties.forEach(prop => {
                if (encryptFields[prop.key] && values[prop.key] && String(values[prop.key]).trim()) {
                    entries.push({key: prop.key, value: String(values[prop.key])});
                }
            });
        });
        return entries;
    };

    const generateProperties = (encryptedValues = {}) => {
        if (!schema) return '';

        const lines = [
            '############################################################',
            '#              config-override.properties                  #',
            `#         ${client || 'Unknown'} - ${serverName || 'Unknown'}`,
            '#         Generated by Into Build Config Generator         #',
            '############################################################',
            '',
        ];

        schema.categories.forEach(cat => {
            const visibleProps = cat.properties.filter(prop => {
                if (prop.virtual) return false;
                if (prop.alwaysInclude) return true;
                if (encryptFields[prop.key] && values[prop.key] && String(values[prop.key]).trim()) return true;
                if (!isFieldVisible(prop)) return false;
                const value = values[prop.key];
                if (value === prop.default) return false;
                if (value === '' && prop.default === '') return false;
                return true;
            });

            if (visibleProps.length > 0) {
                lines.push(`# ${cat.label}`);
                visibleProps.forEach(prop => {
                    let value = encryptedValues[prop.key] || values[prop.key];
                    if (typeof value === 'boolean') {
                        value = value ? 'true' : 'false';
                    }
                    lines.push(`${prop.key}=${value}`);
                });
                lines.push('');
            }
        });

        return lines.join('\n');
    };

    const encryptCheckedFields = async () => {
        const entries = getEncryptEntries();
        if (entries.length === 0) return {};

        const res = await configApi.encryptValues(entries);
        return res.data.encrypted;
    };

    const handleDownload = async () => {
        setEncrypting(true);
        try {
            const encryptedValues = await encryptCheckedFields();
            const content = generateProperties(encryptedValues);
            const blob = new Blob([content], {type: 'text/plain;charset=utf-8'});
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `config-override_${client || 'config'}_${serverName || 'server'}.properties`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('config-override.properties 파일이 다운로드되었습니다.');
        } catch (err) {
            toast.error('암호화 처리 중 오류가 발생했습니다.');
            console.error(err);
        } finally {
            setEncrypting(false);
        }
    };

    const handlePreview = async () => {
        setEncrypting(true);
        try {
            const encryptedValues = await encryptCheckedFields();
            const content = generateProperties(encryptedValues);
            const win = window.open('', '_blank');
            win.document.write(`<pre style="font-family: monospace; padding: 20px;">${content}</pre>`);
        } catch (err) {
            toast.error('암호화 처리 중 오류가 발생했습니다.');
            console.error(err);
        } finally {
            setEncrypting(false);
        }
    };

    const handleSave = async () => {
        if (!client.trim()) {
            toast.error('고객사를 입력해주세요.');
            return;
        }
        if (!serverName.trim()) {
            toast.error('서버명을 입력해주세요.');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                product: selectedProduct,
                client: client.trim(),
                serverName: serverName.trim(),
                description: description.trim(),
                configData: values,
            };

            if (editingPreset) {
                await api.put(`/api/config/presets/${editingPreset.id}`, payload);
                toast.success('설정이 수정되었습니다.');
            } else {
                await api.post('/api/config/presets', payload);
                toast.success('설정이 저장되었습니다.');
            }

            onSaveComplete();
        } catch (err) {
            toast.error('저장에 실패했습니다.');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (!schema) return;
        const initial = {};
        schema.categories.forEach(cat => {
            cat.properties.forEach(prop => {
                initial[prop.key] = prop.default;
            });
        });
        setValues(initial);
        toast.info('설정이 초기화되었습니다.');
    };

    const currentCategory = useMemo(() => {
        if (!schema) return null;
        return schema.categories.find(c => c.id === selectedCategory);
    }, [schema, selectedCategory]);

    const renderField = (prop) => {
        if (!isFieldVisible(prop)) return null;

        const value = values[prop.key];

        switch (prop.type) {
            case 'boolean':
                return (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(value)}
                                onChange={(e) => handleChange(prop.key, e.target.checked)}
                            />
                        }
                        label={prop.label}
                    />
                );

            case 'select':
                const options = Array.isArray(prop.options)
                    ? prop.options.map(opt => typeof opt === 'string' ? {value: opt, label: opt} : opt)
                    : [];
                return (
                    <FormControl fullWidth size="small">
                        <InputLabel>{prop.label}</InputLabel>
                        <Select
                            value={value || ''}
                            onChange={(e) => handleChange(prop.key, e.target.value)}
                            label={prop.label}
                        >
                            {options.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );

            case 'number':
                return (
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={prop.label}
                        value={value ?? ''}
                        onChange={(e) => handleChange(prop.key, e.target.value ? Number(e.target.value) : '')}
                        placeholder={prop.placeholder}
                        InputProps={{
                            endAdornment: prop.unit && (
                                <InputAdornment position="end">{prop.unit}</InputAdornment>
                            ),
                        }}
                    />
                );

            case 'password':
                return (
                    <TextField
                        fullWidth
                        size="small"
                        type={showPasswords[prop.key] ? 'text' : 'password'}
                        label={prop.label}
                        value={value || ''}
                        onChange={(e) => handleChange(prop.key, e.target.value)}
                        placeholder={prop.placeholder}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => togglePasswordVisibility(prop.key)}
                                    >
                                        {showPasswords[prop.key] ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                );

            default:
                return (
                    <TextField
                        fullWidth
                        size="small"
                        label={prop.label}
                        value={value || ''}
                        onChange={(e) => handleChange(prop.key, e.target.value)}
                        placeholder={prop.placeholder}
                        disabled={prop.readOnly}
                    />
                );
        }
    };

    const categoryListContent = schema ? (
        <List dense>
            {schema.categories.map((cat) => (
                <ListItemButton
                    key={cat.id}
                    selected={selectedCategory === cat.id}
                    onClick={() => {
                        setSelectedCategory(cat.id);
                        if (isMobile) setMobileDrawerOpen(false);
                    }}
                >
                    <ListItemIcon sx={{minWidth: 36}}>
                        {categoryIcons[cat.id] || <SettingsIcon/>}
                    </ListItemIcon>
                    <ListItemText
                        primary={cat.label}
                        primaryTypographyProps={{fontSize: '0.875rem'}}
                    />
                </ListItemButton>
            ))}
        </List>
    ) : null;

    if (!schema) {
        return (
            <Typography color="text.secondary">
                스키마를 불러올 수 없습니다.
            </Typography>
        );
    }

    return (
        <Box>
            {/* 헤더 */}
            {editingPreset && (
                <Box sx={styles.header}>
                    <IconButton onClick={onClose}>
                        <ArrowBackIcon/>
                    </IconButton>
                    <Typography variant="h6">
                        설정 수정: {editingPreset.client} - {editingPreset.server_name}
                    </Typography>
                </Box>
            )}

            {/* 메타 정보 입력 */}
            <Paper variant="outlined" sx={styles.metaForm}>
                <Typography variant="subtitle2" sx={{mb: 2, fontWeight: 600}}>
                    기본 정보
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small" disabled={!!editingPreset}>
                            <InputLabel>제품</InputLabel>
                            <Select
                                value={selectedProduct}
                                onChange={(e) => setSelectedProduct(e.target.value)}
                                label="제품"
                            >
                                {productKeys.map((key) => (
                                    <MenuItem key={key} value={key}>
                                        {productLabels[key] || key}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            freeSolo
                            options={clientOptions}
                            value={client}
                            onChange={(e, newValue) => setClient(newValue || '')}
                            onInputChange={(e, newValue) => setClient(newValue || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label="고객사 *"
                                    placeholder="예: 한국토지주택공사"
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="서버명 *"
                            value={serverName}
                            onChange={(e) => setServerName(e.target.value)}
                            placeholder="예: 운영서버, 개발서버"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="설명"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="예: 2024년 구축"
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* 설정 폼 */}
            <Box sx={styles.container}>
                {/* Desktop: 좌측 카테고리 목록 */}
                {!isMobile && (
                    <Box sx={styles.categoryList}>
                        <Paper variant="outlined" sx={styles.categoryPaper}>
                            {categoryListContent}
                        </Paper>
                    </Box>
                )}

                {/* Mobile: Drawer */}
                {isMobile && (
                    <>
                        <Drawer
                            anchor="left"
                            open={mobileDrawerOpen}
                            onClose={() => setMobileDrawerOpen(false)}
                        >
                            <Box sx={{width: 250, pt: 2}}>
                                <Typography sx={{px: 2, pb: 1, fontWeight: 600}}>
                                    설정 카테고리
                                </Typography>
                                {categoryListContent}
                            </Box>
                        </Drawer>
                        <Fab
                            color="primary"
                            sx={styles.fab}
                            onClick={() => setMobileDrawerOpen(true)}
                        >
                            <MenuIcon/>
                        </Fab>
                    </>
                )}

                {/* 우측 설정 폼 */}
                <Box sx={styles.content}>
                    {currentCategory && (
                        <Box sx={styles.section}>
                            <Typography sx={styles.sectionTitle}>
                                {currentCategory.label}
                            </Typography>
                            <Typography sx={styles.sectionDesc}>
                                {currentCategory.description}
                            </Typography>
                            <Divider sx={{mb: 3}}/>

                            <Grid container spacing={2}>
                                {currentCategory.properties.map((prop) => {
                                    if (prop.hidden) return null;
                                    if (!isFieldVisible(prop)) return null;

                                    return (
                                        <Grid item xs={12} md={prop.type === 'boolean' ? 6 : 12} key={prop.key}>
                                            <Box sx={styles.fieldRow}>
                                                {prop.type !== 'boolean' && (
                                                    <Box sx={styles.fieldLabel}>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {prop.label}
                                                            {prop.required && <span style={{color: 'red'}}> *</span>}
                                                        </Typography>
                                                        {prop.description && (
                                                            <Tooltip title={prop.description} arrow>
                                                                <HelpOutlineIcon
                                                                    sx={{
                                                                        fontSize: 16,
                                                                        color: 'text.secondary',
                                                                        cursor: 'help'
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                )}
                                                <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 0.5}}>
                                                    <Box sx={{flexGrow: 1}}>
                                                        {renderField(prop)}
                                                    </Box>
                                                    {prop.type !== 'boolean' && !prop.virtual && (
                                                        <Tooltip title={prop.alwaysEncrypt ? '필수 암호화 항목' : (encryptFields[prop.key] ? 'ENC 암호화 해제' : 'ENC 암호화 적용')} arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => !prop.alwaysEncrypt && toggleEncryptField(prop.key)}
                                                                sx={{
                                                                    mt: 0.5,
                                                                    color: encryptFields[prop.key] ? 'warning.main' : 'action.disabled',
                                                                    cursor: prop.alwaysEncrypt ? 'default' : 'pointer',
                                                                }}
                                                            >
                                                                {encryptFields[prop.key] ? <LockIcon fontSize="small"/> : <LockOpenIcon fontSize="small"/>}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                {prop.hint && (
                                                    <Typography sx={styles.fieldHint}>
                                                        {prop.hint}
                                                    </Typography>
                                                )}
                                                {prop.examples && (
                                                    <Typography sx={styles.fieldHint}>
                                                        예시: {Object.entries(prop.examples).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    )}

                    <Box sx={styles.actions}>
                        <Button
                            variant="outlined"
                            onClick={handleReset}
                        >
                            초기화
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handlePreview}
                            disabled={encrypting}
                        >
                            미리보기
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon/>}
                            onClick={handleDownload}
                            disabled={encrypting}
                        >
                            {encrypting ? '암호화 중...' : '다운로드'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon/>}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {editingPreset ? '수정' : '저장'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
